export interface AudioInputOption {
  deviceId: string;
  label: string;
}

export interface AcquiredAudioInput {
  stream: MediaStream;
  fellBackToDefault: boolean;
}

export function audioConstraint(deviceId: string): MediaTrackConstraints | true {
  return deviceId ? { deviceId: { exact: deviceId } } : true;
}

export function normalizeAudioInputs(devices: MediaDeviceInfo[]): AudioInputOption[] {
  const seen = new Set<string>();
  let unnamed = 0;
  return devices.flatMap((device) => {
    if (device.kind !== 'audioinput' || !device.deviceId || seen.has(device.deviceId)) return [];
    seen.add(device.deviceId);
    unnamed += 1;
    return [{ deviceId: device.deviceId, label: device.label || `Mikrofon ${unnamed}` }];
  });
}

export async function acquireAudioInput(
  deviceId: string,
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>,
): Promise<AcquiredAudioInput> {
  try {
    return {
      stream: await getUserMedia({ audio: audioConstraint(deviceId) }),
      fellBackToDefault: false,
    };
  } catch (error) {
    if (!deviceId) throw error;
    return {
      stream: await getUserMedia({ audio: true }),
      fellBackToDefault: true,
    };
  }
}

/**
 * Replaces the WebRTC sender first and only then stops the previous stream.
 * This ordering prevents a failed AirPods switch from destroying the working
 * microphone. A rejected replacement cleans up only the newly acquired stream.
 */
export async function replaceAudioInput(
  previous: MediaStream | null,
  next: MediaStream,
  sender: RTCRtpSender,
  muted: boolean,
): Promise<MediaStreamTrack> {
  const track = next.getAudioTracks()[0];
  if (!track) {
    next.getTracks().forEach((item) => item.stop());
    throw new Error('Kein Audio-Track im gewählten Eingang');
  }
  track.enabled = !muted;
  try {
    await sender.replaceTrack(muted ? null : track);
  } catch (error) {
    next.getTracks().forEach((item) => item.stop());
    throw error;
  }
  previous?.getTracks().forEach((item) => item.stop());
  return track;
}

export function activeAudioInput(stream: MediaStream, options: AudioInputOption[]): AudioInputOption {
  const track = stream.getAudioTracks()[0];
  const deviceId = String(track?.getSettings().deviceId || '');
  const known = options.find((option) => option.deviceId === deviceId);
  return {
    deviceId,
    label: known?.label || track?.label || 'System-Mikrofon',
  };
}

export function shouldReacquireAudioInput(deviceId: string, options: AudioInputOption[]): boolean {
  return !deviceId || !options.some((option) => option.deviceId === deviceId);
}

export function friendlyAudioInputError(error: unknown): string {
  const name = error instanceof DOMException ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Mikrofonzugriff im Browser blockiert — Website-Berechtigung für cloud2 prüfen.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'Gewähltes Mikrofon ist nicht verbunden oder nicht verfügbar.';
  }
  if (name === 'NotReadableError' || name === 'AbortError') {
    return 'Mikrofon kann nicht geöffnet werden — möglicherweise verwendet es gerade eine andere App.';
  }
  return error instanceof Error ? error.message : 'Mikrofonzugriff fehlgeschlagen';
}
